import { useEffect, useMemo, useState } from 'react';
import { Edit, Eye, Plus, Search, Trash2, FolderOpen, MinusCircle } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { ClientRecord, FamilyMember } from './types/clientTypes';
import { deleteClient, emptyClient, findClientDuplicates, listClients, saveClient } from './services/clientService';

type ReminderItem = { id: string; clientName: string; type: 'Birthday' | 'Anniversary' | 'Passport'; date: string; daysLeft: number };

function validate(form: Partial<ClientRecord>) {
  if (!form.firstName?.trim()) return 'First Name is required.';
  if (!form.lastName?.trim()) return 'Last Name is required.';
  if (!form.phoneNumber?.trim()) return 'Phone is required.';
  return '';
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function nextAnnualDate(dateString: string) {
  if (!dateString) return null;
  const original = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(original.getTime())) return null;
  const today = startOfToday();
  let next = new Date(today.getFullYear(), original.getMonth(), original.getDate());
  if (next < today) next = new Date(today.getFullYear() + 1, original.getMonth(), original.getDate());
  return next;
}

function daysBetween(target: Date) {
  return Math.ceil((target.getTime() - startOfToday().getTime()) / 86400000);
}

function annualReminder(c: ClientRecord, type: 'Birthday' | 'Anniversary', date: string): ReminderItem | null {
  const next = nextAnnualDate(date);
  if (!next) return null;
  const daysLeft = daysBetween(next);
  if (daysLeft < 0 || daysLeft > 30) return null;
  return { id: `${c.id}-${type}`, clientName: c.fullName, type, date, daysLeft };
}

function expiryReminder(c: ClientRecord): ReminderItem | null {
  if (!c.passportExpiry) return null;
  const d = new Date(`${c.passportExpiry}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const daysLeft = daysBetween(d);
  if (daysLeft < 0 || daysLeft > 240) return null;
  return { id: `${c.id}-Passport`, clientName: c.fullName, type: 'Passport', date: c.passportExpiry, daysLeft };
}

function formatFamily(list?: FamilyMember[]) {
  if (!list?.length) return '-';
  return list.map(m => `${m.name || '-'} (${m.relation || '-'}) ${m.phone || ''}`).join(' | ');
}

export default function ClientMasterPage({ user }: { user: ERPUser }) {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const [viewing, setViewing] = useState<ClientRecord | null>(null);
  const [editing, setEditing] = useState<ClientRecord | null>(null);
  const [form, setForm] = useState<Partial<ClientRecord>>(emptyClient());
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() { setClients(await listClients()); }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter(c => !q || [c.fullName, c.phoneFull, c.email, c.usCity, c.indiaCity, c.clientCode].join(' ').toLowerCase().includes(q));
  }, [clients, search]);

  const dateReminders = useMemo(() => clients.flatMap(c => [
    annualReminder(c, 'Birthday', c.birthDate),
    annualReminder(c, 'Anniversary', c.anniversary)
  ].filter(Boolean) as ReminderItem[]).sort((a,b) => a.daysLeft - b.daysLeft).slice(0, 10), [clients]);

  const passportReminders = useMemo(() => clients.map(expiryReminder).filter(Boolean).sort((a,b) => (a as ReminderItem).daysLeft - (b as ReminderItem).daysLeft).slice(0, 10) as ReminderItem[], [clients]);

  function beginAdd() { setEditing(null); setViewing(null); setMore(false); setForm(emptyClient()); setMsg(''); setOpen(true); }
  function beginEdit(client: ClientRecord) { setEditing(client); setViewing(null); setMore(false); setForm(client); setMsg(''); setOpen(true); }

  async function save() {
    const err = validate(form);
    if (err) { setMsg(err); return; }
    setBusy(true);
    try {
      if (!editing) {
        const dups = await findClientDuplicates(form);
        if (dups.length && !window.confirm(`Possible duplicate client found: ${dups[0].fullName}. Save anyway?`)) return;
      }
      await saveClient({ ...form, fullName: `${form.firstName || ''} ${form.lastName || ''}`.trim() }, user);
      setOpen(false);
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Unable to save client.');
    } finally { setBusy(false); }
  }

  function setPassport(field: string, value: string) {
    setForm({ ...form, passportData: { ...(form.passportData || emptyClient().passportData!), [field]: value } as any, passportExpiry: field === 'expiryDate' ? value : form.passportExpiry });
  }
  function setCard(field: string, value: string) { setForm({ ...form, ccData: { ...(form.ccData || emptyClient().ccData!), [field]: value } as any }); }
  function addFamilyMember() { setForm({ ...form, familyMembers: [...(form.familyMembers || []), { name: '', relation: '', phone: '' }] }); }
  function updateFamilyMember(index: number, key: keyof FamilyMember, value: string) { const list = [...(form.familyMembers || [])]; list[index] = { ...list[index], [key]: value }; setForm({ ...form, familyMembers: list }); }
  function removeFamilyMember(index: number) { const list = [...(form.familyMembers || [])]; list.splice(index, 1); setForm({ ...form, familyMembers: list }); }

  return (
    <div className="page">
      <div className="page-head">
        <div><p className="eyebrow">Phase 3.3</p><h1>Client Master V2</h1><p>First Name, Last Name, Phone required. Reminder logic now shows only due events.</p></div>
        <button className="btn primary" onClick={beginAdd} title="Add new client"><Plus size={18}/> Add Client</button>
      </div>

      <div className="reminder-top-grid">
        <section className="card reminder-card">
          <h3>Upcoming 30 Days</h3>
          {dateReminders.length === 0 ? <p className="muted">No birthday / anniversary reminders.</p> : dateReminders.map(r => <p key={r.id}><b>{r.clientName}</b><br/><small>{r.type}: {r.date} ({r.daysLeft === 0 ? 'Today' : `${r.daysLeft} days`})</small></p>)}
        </section>
        <section className="card reminder-card">
          <h3>Passport Expiring 8 Months</h3>
          {passportReminders.length === 0 ? <p className="muted">No passport reminders.</p> : passportReminders.map(r => <p key={r.id}><b>{r.clientName}</b><br/><small>Passport Expiry: {r.date} ({r.daysLeft} days)</small></p>)}
        </section>
      </div>

      <section>
        <div className="toolbar"><div style={{position:'relative'}}><Search size={18} style={{position:'absolute',left:16,top:18,color:'#66758a'}}/><input style={{paddingLeft:44}} placeholder="Search name, phone, email, city" value={search} onChange={e => setSearch(e.target.value)} /></div></div>
        <section className="card table-card" style={{marginTop:20}}>
          <table><thead><tr><th>Client</th><th>US City</th><th>India City</th><th>Phone</th><th>Email</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="empty">No clients found.</td></tr>}
            {filtered.map(c => <tr key={c.id}>
              <td><strong>{c.fullName}</strong><br/><small>{c.clientCode}</small></td><td>{c.usCity || '-'}</td><td>{c.indiaCity || '-'}</td><td>{c.phoneFull || '-'}</td><td>{c.email || '-'}</td>
              <td className="actions">
                <button title="View client full details" onClick={() => setViewing(c)}><Eye size={16}/></button>
                <button title="Edit client" onClick={() => beginEdit(c)}><Edit size={16}/></button>
                <button title="View client documents - Phase 5" onClick={() => {
    window.history.pushState(null, "", "/documents?ownerType=client");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }}><FolderOpen size={16}/></button>
                <button title="Delete client" onClick={async () => { if(confirm('Delete this client?')) { await deleteClient(c.id); await load(); } }}><Trash2 size={16}/></button>
              </td>
            </tr>)}
          </tbody></table>
        </section>
      </section>

      {open && <div className="modal-backdrop"><div className="modal large">
        <div className="modal-head"><h2>{editing ? 'Edit Client' : 'Add Client'}</h2><button className="icon-only" title="Close" onClick={() => setOpen(false)}>×</button></div>
        {msg && <div className="error-box" style={{margin:'18px 30px'}}>{msg}</div>}

        <div className="form-section-title">Main Details</div>
        <div className="form-grid">
          <label>First Name *<input value={form.firstName || ''} onChange={e => setForm({...form, firstName:e.target.value})}/></label>
          <label>Last Name *<input value={form.lastName || ''} onChange={e => setForm({...form, lastName:e.target.value})}/></label>
          <label>Email<input value={form.email || ''} onChange={e => setForm({...form, email:e.target.value})}/></label>
          <label>Country Code<input value={form.phoneCountryCode || '+1'} onChange={e => setForm({...form, phoneCountryCode:e.target.value})}/></label>
          <label>Phone *<input value={form.phoneNumber || ''} onChange={e => setForm({...form, phoneNumber:e.target.value})}/></label>
          <label>Other Phone<input value={form.otherPhone || ''} onChange={e => setForm({...form, otherPhone:e.target.value})}/></label>
          <label>US City<input value={form.usCity || ''} onChange={e => setForm({...form, usCity:e.target.value})}/></label>
          <label>India City<input value={form.indiaCity || ''} onChange={e => setForm({...form, indiaCity:e.target.value})}/></label>
          <label>Birth Date<input type="date" value={form.birthDate || ''} onChange={e => setForm({...form, birthDate:e.target.value})}/></label>
          <label className="span-3">Note<textarea value={form.note || ''} onChange={e => setForm({...form, note:e.target.value})}/></label>
        </div>

        <div className="family-box"><div className="family-head"><h3>Family Members</h3><button className="btn" onClick={addFamilyMember} title="Add family member"><Plus size={16}/> Add Family Member</button></div>
          {(form.familyMembers || []).map((m, idx) => <div className="family-row" key={idx}><input placeholder="Name" value={m.name} onChange={e => updateFamilyMember(idx, 'name', e.target.value)} /><input placeholder="Relation" value={m.relation} onChange={e => updateFamilyMember(idx, 'relation', e.target.value)} /><input placeholder="Phone No" value={m.phone} onChange={e => updateFamilyMember(idx, 'phone', e.target.value)} /><button title="Remove family member" onClick={() => removeFamilyMember(idx)}><MinusCircle size={18}/></button></div>)}
        </div>

        <div className="more-toggle"><button className="btn" title="Open secondary client fields" onClick={() => setMore(!more)}>{more ? 'Hide More Details' : '+ More Details'}</button></div>
        {more && <><div className="form-section-title">Secondary Details</div><div className="form-grid">
          <label>Label<select value={form.label || 'general'} onChange={e => setForm({...form, label:e.target.value})}><option>general</option><option>vip</option><option>repeat_client</option><option>family</option><option>corporate</option><option>referral</option><option>cruise</option><option>package</option><option>flight</option></select></label>
          <label>Anniversary<input type="date" value={form.anniversary || ''} onChange={e => setForm({...form, anniversary:e.target.value})}/></label>
          <label>Visa Expiry<input type="date" value={form.visaExpiry || ''} onChange={e => setForm({...form, visaExpiry:e.target.value})}/></label>
          <label className="span-3">Address<input value={form.address || ''} onChange={e => setForm({...form, address:e.target.value})}/></label>
          <label>Passport No<input value={form.passportData?.pptNo || ''} onChange={e => setPassport('pptNo', e.target.value)}/></label><label>Nationality<input value={form.passportData?.nationality || ''} onChange={e => setPassport('nationality', e.target.value)}/></label><label>Passport DOB<input value={form.passportData?.dob || ''} placeholder="ddmmmyyyy" onChange={e => setPassport('dob', e.target.value)}/></label>
          <label>Passport Expiry<input type="date" value={form.passportData?.expiryDate || form.passportExpiry || ''} onChange={e => setPassport('expiryDate', e.target.value)}/></label><label>Gender<select value={form.passportData?.gender || ''} onChange={e => setPassport('gender', e.target.value)}><option value="">Select</option><option>M</option><option>F</option></select></label><label>Photo URL<input value={form.photoUrl || ''} onChange={e => setForm({...form, photoUrl:e.target.value})}/></label>
          <label>CC No<input value={form.ccData?.no || ''} onChange={e => setCard('no', e.target.value)}/></label><label>Name on Card<input value={form.ccData?.nameOnCard || ''} onChange={e => setCard('nameOnCard', e.target.value)}/></label><label>CC Expiry<input value={form.ccData?.expiry || ''} onChange={e => setCard('expiry', e.target.value)}/></label><label>CC Code<input value={form.ccData?.code || ''} onChange={e => setCard('code', e.target.value)}/></label><label>CC Type<input value={form.ccData?.type || ''} onChange={e => setCard('type', e.target.value)}/></label><label>CC Address<input value={form.ccData?.address || ''} onChange={e => setCard('address', e.target.value)}/></label>
        </div></>}
        <div className="modal-foot"><button className="btn" onClick={() => setOpen(false)}>Cancel</button><button className="btn primary" disabled={busy} onClick={save}>{busy ? 'Saving...' : 'Save Client'}</button></div>
      </div></div>}

      {viewing && <div className="modal-backdrop"><div className="modal large">
        <div className="modal-head"><h2>{viewing.fullName}</h2><button className="icon-only" title="Close" onClick={() => setViewing(null)}>×</button></div>
        <div className="form-section-title">Main Client Details</div>
        <div className="form-grid">
          <div><b>Client Code</b><p>{viewing.clientCode || '-'}</p></div><div><b>Label</b><p>{viewing.label || '-'}</p></div><div><b>Status</b><p>{viewing.status || '-'}</p></div>
          <div><b>Phone</b><p>{viewing.phoneFull || '-'}</p></div><div><b>Other Phone</b><p>{viewing.otherPhone || '-'}</p></div><div><b>Email</b><p>{viewing.email || '-'}</p></div>
          <div><b>US City</b><p>{viewing.usCity || '-'}</p></div><div><b>India City</b><p>{viewing.indiaCity || '-'}</p></div><div><b>Birth Date</b><p>{viewing.birthDate || '-'}</p></div>
          <div className="span-3"><b>Family Members</b><p>{formatFamily(viewing.familyMembers)}</p></div><div className="span-3"><b>Note</b><p>{viewing.note || '-'}</p></div>
        </div>
        <div className="form-section-title">Secondary Details</div>
        <div className="form-grid">
          <div><b>Anniversary</b><p>{viewing.anniversary || '-'}</p></div><div><b>Visa Expiry</b><p>{viewing.visaExpiry || '-'}</p></div><div><b>Photo URL</b><p>{viewing.photoUrl || '-'}</p></div>
          <div className="span-3"><b>Address</b><p>{viewing.address || '-'}</p></div>
          <div><b>Passport No</b><p>{viewing.passportData?.pptNo || '-'}</p></div><div><b>Nationality</b><p>{viewing.passportData?.nationality || '-'}</p></div><div><b>Passport DOB</b><p>{viewing.passportData?.dob || '-'}</p></div>
          <div><b>Passport Expiry</b><p>{viewing.passportExpiry || viewing.passportData?.expiryDate || '-'}</p></div><div><b>Gender</b><p>{viewing.passportData?.gender || '-'}</p></div><div><b>CC Type</b><p>{viewing.ccData?.type || '-'}</p></div>
          <div><b>CC No</b><p>{viewing.ccData?.no || '-'}</p></div><div><b>Name on Card</b><p>{viewing.ccData?.nameOnCard || '-'}</p></div><div><b>CC Expiry</b><p>{viewing.ccData?.expiry || '-'}</p></div><div><b>CC Code</b><p>{viewing.ccData?.code || '-'}</p></div><div className="span-3"><b>CC Address</b><p>{viewing.ccData?.address || '-'}</p></div>
        </div>
        <div className="modal-foot"><button className="btn" title="Open client document vault" onClick={() => {
    window.history.pushState(null, "", "/documents?ownerType=client");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }}>View Client Documents</button><button className="btn primary" title="Edit this client" onClick={() => { setViewing(null); beginEdit(viewing); }}>Edit Client</button></div>
      </div></div>}
    </div>
  );
}
